
import Vue from "vue";
import { Component, Prop, Watch } from "vue-property-decorator";
import DropdownInput from "@/components/Reusable/CustomInputs/DropdownInput.vue";
import TextInput from "@/components/Reusable/CustomInputs/TextInput.vue";
import { DropdownOptions } from "@/types/controls";
import DatePickerInput from "@/components/Reusable/CustomInputs/DatePickerInput.vue";
import MultiselectInput from "@/components/Reusable/CustomInputs/MultiselectInput.vue";
import TimePickerInput from "@/components/Reusable/CustomInputs/TimePickerInput.vue";

type Field = {
  type: string;
  label: string;
  value: string;
  options: { label: string; value: string }[];
  advanced: { id: string };
};

type ConditionalValue = {
  field: Field | null;
  operand: string | null;
  value: string | string[] | null;
};

const DEFAULT_VALUE = {
  field: null,
  operand: null,
  value: null
};

@Component({
  components: {
    TimePickerInput,
    MultiselectInput,
    DatePickerInput,
    DropdownInput,
    TextInput
  }
})
export default class ConditionalFieldInput extends Vue {
  @Prop()
  value!: ConditionalValue | null;

  @Prop()
  dropdownOptions!: DropdownOptions;

  @Prop()
  fieldId!: string | null;

  internalValue: ConditionalValue = this.value || DEFAULT_VALUE;

  @Watch("value")
  updateInternalValue() {
    this.internalValue = this.value || DEFAULT_VALUE;
  }

  get fieldValue() {
    return this.internalValue?.field?.advanced?.id;
  }

  get valueAsArray() {
    return Array.isArray(this.internalValue?.value)
      ? this.internalValue.value
      : null;
  }

  get operandOptions() {
    if (this.value?.field === null) {
      return [];
    }

    if (this.value?.field.type === "file") {
      return [
        { text: "is set", value: "is set" },
        { text: "is not set", value: "is not set" }
      ];
    }

    const options = [
      { text: "equals", value: "equals" },
      { text: "not equals", value: "not equals" },
      { text: "is set", value: "is set" },
      { text: "is not set", value: "is not set" }
    ];
    if (this.hasStringValue) {
      options.push(
        { text: "is one of", value: "is one of" },
        { text: "is none of", value: "is none of" },
        { text: "contains", value: "contains" },
        {
          text: "does not contain",
          value: "does not contain"
        }
      );
    }

    if (this.hasDateValue) {
      options.push(
        { text: "is before", value: "is before date" },
        { text: "is after", value: "is after date" }
      );
    }

    if (this.hasTimeValue) {
      options.push(
        { text: "is before", value: "is before time" },
        { text: "is after", value: "is after time" }
      );
    }

    if (this.hasNumericValue) {
      options.push(
        { text: "is greater than", value: "is greater than" },
        { text: "is less than", value: "is less than" }
      );
    }

    return options;
  }

  get multiSelectOperand() {
    return (
      this.value?.operand === "is one of" ||
      this.value?.operand === "is none of"
    );
  }

  editField(field: any) {
    this.internalValue.field = field;
    this.internalValue.value = DEFAULT_VALUE.value;
    this.internalValue.operand = DEFAULT_VALUE.operand;

    this.$emit("input", this.internalValue);
  }

  editOperand(operand: string) {
    this.internalValue.operand = operand;

    this.$emit("input", this.internalValue);
  }

  editValue(value: string | string[]) {
    this.internalValue.value = value;

    this.$emit("input", this.internalValue);
  }

  get field() {
    return this.value?.field;
  }

  get internalDropdownOptions(): DropdownOptions {
    if (!this.dropdownOptions) {
      return this.dropdownOptions;
    }

    const internalDropdownOptions = this.dropdownOptions;
    if (internalDropdownOptions.populate) {
      internalDropdownOptions.populate.condition = {
        path: "advanced.id",
        operand: "not equals",
        value: this.fieldId
      };
    }

    return internalDropdownOptions;
  }

  get fieldOptions() {
    return this.value?.field?.options.map(option => {
      return {
        text: option.label,
        value: option.value
      };
    });
  }

  get hasOptionValue() {
    return this.field?.options;
  }

  get hasNumericValue() {
    return this.field?.type === "number";
  }

  get hasDateValue() {
    return this.field?.type === "date";
  }

  get hasTimeValue() {
    return this.field?.type === "time";
  }

  get hasStringValue() {
    return (
      !this.hasDateValue &&
      !this.hasTimeValue &&
      !this.hasOptionValue &&
      !this.hasNoValue
    );
  }

  get hasNoValue() {
    return (
      this.internalValue.operand === "is set" ||
      this.internalValue.operand === "is not set"
    );
  }
}
